import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';

const SECTIONS = [
  {
    id: 'information-we-collect',
    icon: 'ri-database-2-line',
    color: 'text-[#00FFD1]',
    border: 'border-[#00FFD1]/30',
    bg: 'bg-[#00FFD1]/10',
    title: '1. Information We Collect',
    content: (
      <>
        <p>
          When you join the WingmanFix waitlist or use our services, we may collect the following categories of information:
        </p>
        <h4 className="text-white font-semibold mt-4 mb-2">Information You Provide Directly</h4>
        <ul className="space-y-2 ml-4">
          {[
            'Full name and email address',
            'Pilot role, certificate level, and flying hours',
            'Primary aircraft type and home airport (ICAO/IATA)',
            'Training goals and how you heard about us',
            'Any other information you voluntarily submit through our forms',
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <i className="ri-arrow-right-s-line text-[#00FFD1] text-lg flex-shrink-0 mt-0.5" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <h4 className="text-white font-semibold mt-4 mb-2">Information Collected Automatically</h4>
        <ul className="space-y-2 ml-4">
          {[
            'Browser type, device type, and operating system',
            'IP address and approximate geographic location',
            'Pages visited, time spent, and referral source',
            'Cookies and similar tracking technologies',
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <i className="ri-arrow-right-s-line text-[#00FFD1] text-lg flex-shrink-0 mt-0.5" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <h4 className="text-white font-semibold mt-4 mb-2">Flight & App Data (Beta Users)</h4>
        <ul className="space-y-2 ml-4">
          {[
            'Flight logs, GPS tracks, and performance metrics you upload or sync',
            'Cockpit audio recordings you choose to process through the app',
            'ATC communication transcriptions generated by the app',
            'In-app usage patterns and feature interactions',
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <i className="ri-arrow-right-s-line text-[#00FFD1] text-lg flex-shrink-0 mt-0.5" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </>
    ),
  },
  {
    id: 'how-we-use',
    icon: 'ri-settings-4-line',
    color: 'text-[#00A3E0]',
    border: 'border-[#00A3E0]/30',
    bg: 'bg-[#00A3E0]/10',
    title: '2. How We Use Your Information',
    content: (
      <>
        <p>We use the information we collect for the following purposes:</p>
        <ul className="space-y-2 ml-4 mt-3">
          {[
            'To manage your waitlist position and send your early-access invite',
            'To personalize your onboarding and match you with relevant features',
            'To send product updates, beta announcements, and aviation tips (only if you opted in)',
            'To process referral tracking and award waitlist rewards',
            'To analyze usage patterns and improve the WingmanFix platform',
            'To detect, prevent, and address technical issues or abuse',
            'To comply with legal obligations and enforce our Terms of Use',
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <i className="ri-checkbox-circle-line text-[#00A3E0] text-lg flex-shrink-0 mt-0.5" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <p className="mt-4">
          <strong className="text-white">We will never sell your personal information to third parties.</strong> We do not use your data for advertising profiling or share it with data brokers.
        </p>
      </>
    ),
  },
  {
    id: 'data-sharing',
    icon: 'ri-share-line',
    color: 'text-[#B4FF39]',
    border: 'border-[#B4FF39]/30',
    bg: 'bg-[#B4FF39]/10',
    title: '3. Data Sharing & Disclosure',
    content: (
      <>
        <p>
          We do not sell, rent, or trade your personal information. We may share your data only in the following limited circumstances:
        </p>
        <ul className="space-y-3 ml-4 mt-3">
          {[
            {
              label: 'Service Providers',
              desc: 'Trusted third-party vendors who help us operate the platform (e.g., email delivery, analytics, cloud hosting). They are contractually bound to protect your data and may only use it to perform services on our behalf.',
            },
            {
              label: 'Legal Requirements',
              desc: 'If required by law, court order, or governmental authority, we may disclose information as necessary to comply with legal obligations.',
            },
            {
              label: 'Business Transfers',
              desc: 'In the event of a merger, acquisition, or sale of assets, your data may be transferred as part of that transaction. We will notify you before your data is subject to a different privacy policy.',
            },
            {
              label: 'Safety & Security',
              desc: 'To protect the rights, property, or safety of WingmanFix, our users, or the public when we believe disclosure is necessary.',
            },
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <i className="ri-arrow-right-s-line text-[#B4FF39] text-lg flex-shrink-0 mt-0.5" />
              <span>
                <strong className="text-white">{item.label}:</strong> {item.desc}
              </span>
            </li>
          ))}
        </ul>
      </>
    ),
  },
  {
    id: 'cookies',
    icon: 'ri-cookie-line',
    color: 'text-[#00FFD1]',
    border: 'border-[#00FFD1]/30',
    bg: 'bg-[#00FFD1]/10',
    title: '4. Cookies & Tracking Technologies',
    content: (
      <>
        <p>
          We use cookies and similar technologies to enhance your experience, understand how visitors use our site, and improve our services. These include:
        </p>
        <ul className="space-y-2 ml-4 mt-3">
          {[
            { type: 'Essential Cookies', desc: 'Required for the website to function correctly. Cannot be disabled.' },
            { type: 'Analytics Cookies', desc: 'Help us understand traffic patterns and user behavior (e.g., page views, session duration).' },
            { type: 'Preference Cookies', desc: 'Remember your settings and choices to personalize your experience.' },
            { type: 'Referral Tracking', desc: 'Track referral codes to credit you for successful waitlist invitations.' },
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <i className="ri-arrow-right-s-line text-[#00FFD1] text-lg flex-shrink-0 mt-0.5" />
              <span>
                <strong className="text-white">{item.type}:</strong> {item.desc}
              </span>
            </li>
          ))}
        </ul>
        <p className="mt-4">
          You can control cookies through your browser settings. Disabling certain cookies may affect the functionality of the site.
        </p>
      </>
    ),
  },
  {
    id: 'data-retention',
    icon: 'ri-time-line',
    color: 'text-[#00A3E0]',
    border: 'border-[#00A3E0]/30',
    bg: 'bg-[#00A3E0]/10',
    title: '5. Data Retention',
    content: (
      <>
        <p>
          We retain your personal information for as long as necessary to fulfill the purposes described in this policy, unless a longer retention period is required by law.
        </p>
        <ul className="space-y-2 ml-4 mt-3">
          {[
            'Waitlist data is retained until you request deletion or the waitlist program ends.',
            'Account data is retained for the duration of your account plus a reasonable period thereafter.',
            'Flight data and recordings are retained until you delete them or close your account.',
            'Analytics data is typically retained in aggregated, anonymized form indefinitely.',
            'Legal and compliance records may be retained for up to 7 years.',
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <i className="ri-checkbox-circle-line text-[#00A3E0] text-lg flex-shrink-0 mt-0.5" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </>
    ),
  },
  {
    id: 'your-rights',
    icon: 'ri-shield-user-line',
    color: 'text-[#B4FF39]',
    border: 'border-[#B4FF39]/30',
    bg: 'bg-[#B4FF39]/10',
    title: '6. Your Rights & Choices',
    content: (
      <>
        <p>
          Depending on your location, you may have the following rights regarding your personal data:
        </p>
        <ul className="space-y-3 ml-4 mt-3">
          {[
            { right: 'Access', desc: 'Request a copy of the personal data we hold about you.' },
            { right: 'Correction', desc: 'Request that we correct inaccurate or incomplete information.' },
            { right: 'Deletion', desc: 'Request that we delete your personal data ("right to be forgotten").' },
            { right: 'Portability', desc: 'Request your data in a structured, machine-readable format.' },
            { right: 'Opt-Out', desc: 'Unsubscribe from marketing emails at any time via the link in any email we send.' },
            { right: 'Restriction', desc: 'Request that we restrict processing of your data in certain circumstances.' },
            { right: 'Objection', desc: 'Object to processing of your data for direct marketing or legitimate interest purposes.' },
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <i className="ri-arrow-right-s-line text-[#B4FF39] text-lg flex-shrink-0 mt-0.5" />
              <span>
                <strong className="text-white">{item.right}:</strong> {item.desc}
              </span>
            </li>
          ))}
        </ul>
        <p className="mt-4">
          To exercise any of these rights, contact us at{' '}
          <a href="mailto:privacy@wingmanfix.com" className="text-[#00FFD1] hover:underline">
            privacy@wingmanfix.com
          </a>
          . We will respond within 30 days.
        </p>
      </>
    ),
  },
  {
    id: 'flight-data',
    icon: 'ri-flight-takeoff-line',
    color: 'text-[#00FFD1]',
    border: 'border-[#00FFD1]/30',
    bg: 'bg-[#00FFD1]/10',
    title: '7. Flight Data & Audio Privacy',
    content: (
      <>
        <p>
          We take the privacy of your flight data and cockpit audio seriously. Here is how we handle it:
        </p>
        <ul className="space-y-2 ml-4 mt-3">
          {[
            'Your flight logs and GPS data are stored securely and are only accessible to you and authorized WingmanFix systems.',
            'Cockpit audio recordings are processed solely to generate transcriptions and training insights for your personal use.',
            'Audio recordings are not shared with third parties, used for advertising, or retained longer than necessary.',
            'You can delete your flight data and recordings at any time from within the app.',
            'Aggregated, anonymized flight performance data may be used to improve our AI models, but never in a way that identifies you.',
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <i className="ri-checkbox-circle-line text-[#00FFD1] text-lg flex-shrink-0 mt-0.5" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <p className="mt-4">
          <strong className="text-white">You are responsible</strong> for ensuring you have the legal right to record cockpit audio in your jurisdiction and obtaining any required consents from passengers or other parties.
        </p>
      </>
    ),
  },
  {
    id: 'security',
    icon: 'ri-lock-password-line',
    color: 'text-[#00A3E0]',
    border: 'border-[#00A3E0]/30',
    bg: 'bg-[#00A3E0]/10',
    title: '8. Data Security',
    content: (
      <>
        <p>
          We implement industry-standard technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These include:
        </p>
        <ul className="space-y-2 ml-4 mt-3">
          {[
            'Encryption of data in transit using TLS/HTTPS',
            'Encryption of sensitive data at rest',
            'Access controls limiting data access to authorized personnel only',
            'Regular security audits and vulnerability assessments',
            'Secure cloud infrastructure with reputable providers',
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <i className="ri-shield-check-line text-[#00A3E0] text-lg flex-shrink-0 mt-0.5" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <p className="mt-4">
          While we strive to protect your data, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security, but we will notify you promptly in the event of a data breach affecting your information.
        </p>
      </>
    ),
  },
  {
    id: 'children',
    icon: 'ri-parent-line',
    color: 'text-[#B4FF39]',
    border: 'border-[#B4FF39]/30',
    bg: 'bg-[#B4FF39]/10',
    title: '9. Children\'s Privacy',
    content: (
      <>
        <p>
          WingmanFix is not directed at children under the age of 13. We do not knowingly collect personal information from children under 13. If you believe we have inadvertently collected information from a child under 13, please contact us immediately at{' '}
          <a href="mailto:privacy@wingmanfix.com" className="text-[#00FFD1] hover:underline">
            privacy@wingmanfix.com
          </a>{' '}
          and we will take steps to delete such information promptly.
        </p>
      </>
    ),
  },
  {
    id: 'international',
    icon: 'ri-global-line',
    color: 'text-[#00FFD1]',
    border: 'border-[#00FFD1]/30',
    bg: 'bg-[#00FFD1]/10',
    title: '10. International Data Transfers',
    content: (
      <>
        <p>
          WingmanFix is operated from the United States. If you are accessing our services from outside the United States, please be aware that your information may be transferred to, stored, and processed in the United States or other countries where our servers or service providers are located.
        </p>
        <p className="mt-3">
          By using our services, you consent to the transfer of your information to countries that may have different data protection laws than your country of residence. We take appropriate safeguards to ensure your data is protected in accordance with this Privacy Policy.
        </p>
        <p className="mt-3">
          For users in the European Economic Area (EEA), we rely on Standard Contractual Clauses (SCCs) approved by the European Commission as the legal basis for international data transfers.
        </p>
      </>
    ),
  },
  {
    id: 'changes',
    icon: 'ri-refresh-line',
    color: 'text-[#00A3E0]',
    border: 'border-[#00A3E0]/30',
    bg: 'bg-[#00A3E0]/10',
    title: '11. Changes to This Policy',
    content: (
      <>
        <p>
          We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. When we make material changes, we will:
        </p>
        <ul className="space-y-2 ml-4 mt-3">
          {[
            'Update the "Effective Date" at the top of this page',
            'Notify waitlist and registered users via email',
            'Display a prominent notice on our website',
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <i className="ri-arrow-right-s-line text-[#00A3E0] text-lg flex-shrink-0 mt-0.5" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <p className="mt-4">
          Your continued use of WingmanFix after any changes constitutes your acceptance of the updated Privacy Policy. We encourage you to review this page periodically.
        </p>
      </>
    ),
  },
  {
    id: 'contact',
    icon: 'ri-mail-line',
    color: 'text-[#B4FF39]',
    border: 'border-[#B4FF39]/30',
    bg: 'bg-[#B4FF39]/10',
    title: '12. Contact Us',
    content: (
      <>
        <p>
          If you have any questions, concerns, or requests regarding this Privacy Policy or how we handle your data, please reach out:
        </p>
        <div className="mt-5 bg-[#0D1B2E] border border-[#B4FF39]/20 rounded-xl p-6 space-y-3">
          <p className="text-lg font-semibold text-white">WingmanFix Privacy Team</p>
          <a
            href="mailto:privacy@wingmanfix.com"
            className="text-[#B4FF39] hover:text-[#c8ff5a] transition-colors inline-flex items-center gap-2"
          >
            <i className="ri-mail-line" />
            privacy@wingmanfix.com
          </a>
          <p className="text-sm text-gray-400">We aim to respond to all privacy inquiries within 30 days.</p>
        </div>
      </>
    ),
  },
];

export default function PrivacyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#08121F] text-white">
      <Navigation />

      <main className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-6">

          {/* Header */}
          <div className="mb-12">
            <div className="inline-flex items-center gap-2 bg-[#00FFD1]/10 border border-[#00FFD1]/30 rounded-full px-4 py-1.5 mb-6">
              <i className="ri-shield-keyhole-line text-[#00FFD1] text-sm" />
              <span className="text-xs text-[#00FFD1] uppercase tracking-widest font-semibold">Privacy Policy</span>
            </div>
            <h1 className="text-5xl font-bold mb-6 leading-tight">
              Your Privacy,<br />
              <span className="text-[#00FFD1]">Our Commitment</span>
            </h1>
            <p className="text-lg text-gray-400 leading-relaxed max-w-2xl">
              We built WingmanFix for pilots who care about performance — and we care just as much about protecting your data. This policy explains exactly what we collect, why, and how you stay in control.
            </p>
            <div className="flex flex-wrap gap-4 mt-6 text-sm text-gray-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line text-[#00FFD1]" />
                <strong className="text-white">Effective Date:</strong>&nbsp;January 1, 2025
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-refresh-line text-[#00FFD1]" />
                <strong className="text-white">Last Updated:</strong>&nbsp;January 1, 2025
              </span>
            </div>
          </div>

          {/* Quick Summary Card */}
          <div className="bg-[#0D1B2E] border border-[#00FFD1]/20 rounded-2xl p-8 mb-12">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-flashlight-line text-[#00FFD1] text-xl" />
              </div>
              <h2 className="text-lg font-bold text-[#00FFD1] uppercase tracking-widest">TL;DR — The Short Version</h2>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { icon: 'ri-close-circle-line', color: 'text-red-400', label: 'We never sell your data' },
                { icon: 'ri-lock-line', color: 'text-[#00FFD1]', label: 'Your flight data is private' },
                { icon: 'ri-mail-close-line', color: 'text-[#00A3E0]', label: 'Unsubscribe anytime' },
                { icon: 'ri-delete-bin-line', color: 'text-[#B4FF39]', label: 'Delete your data anytime' },
                { icon: 'ri-eye-off-line', color: 'text-[#00FFD1]', label: 'No ad tracking or profiling' },
                { icon: 'ri-shield-check-line', color: 'text-[#00A3E0]', label: 'Encrypted & secure storage' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 bg-white/5 rounded-xl px-4 py-3">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} ${item.color} text-lg`} />
                  </div>
                  <span className="text-sm text-gray-300 font-medium">{item.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Table of Contents */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-7 mb-12">
            <h2 className="text-sm font-bold text-[#00FFD1] uppercase tracking-widest mb-4 flex items-center gap-2">
              <i className="ri-list-check-2" /> Table of Contents
            </h2>
            <ol className="grid sm:grid-cols-2 gap-2">
              {SECTIONS.map((s, i) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    className="flex items-center gap-2 text-sm text-gray-400 hover:text-[#00FFD1] transition-colors cursor-pointer group"
                  >
                    <span className="w-5 h-5 flex items-center justify-center rounded-full bg-white/10 text-xs font-bold text-gray-500 group-hover:bg-[#00FFD1]/20 group-hover:text-[#00FFD1] transition-all flex-shrink-0">
                      {i + 1}
                    </span>
                    {s.title.replace(/^\d+\.\s/, '')}
                  </a>
                </li>
              ))}
            </ol>
          </div>

          {/* Sections */}
          <div className="space-y-10">
            {SECTIONS.map((s) => (
              <section key={s.id} id={s.id} className="scroll-mt-28">
                <div className="flex items-start gap-4 mb-4">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${s.bg} border ${s.border} flex-shrink-0`}>
                    <i className={`${s.icon} ${s.color} text-lg`} />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold mb-4">{s.title}</h2>
                    <div className="text-base text-gray-300 leading-relaxed space-y-3">
                      {s.content}
                    </div>
                  </div>
                </div>
                <div className="border-t border-white/5 mt-8" />
              </section>
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="mt-16 bg-gradient-to-br from-[#00FFD1]/10 to-[#00A3E0]/10 border border-[#00FFD1]/20 rounded-2xl p-8 text-center">
            <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 bg-[#00FFD1]/10 rounded-full border border-[#00FFD1]/30">
              <i className="ri-shield-keyhole-line text-[#00FFD1] text-2xl" />
            </div>
            <h3 className="text-xl font-bold mb-2">Questions About Your Privacy?</h3>
            <p className="text-gray-400 text-sm mb-6 max-w-md mx-auto">
              We're committed to transparency. If anything in this policy is unclear, reach out — we'll explain it in plain English.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <a
                href="mailto:privacy@wingmanfix.com"
                className="inline-flex items-center gap-2 bg-[#00FFD1] text-[#060E1E] font-bold px-6 py-3 rounded-lg hover:bg-[#00e6bc] transition-all cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-mail-send-line" /> Contact Privacy Team
              </a>
              <Link
                to="/terms"
                className="inline-flex items-center gap-2 border border-white/20 text-gray-300 hover:text-white hover:border-white/40 font-semibold px-6 py-3 rounded-lg transition-all cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-file-text-line" /> View Terms of Use
              </Link>
            </div>
          </div>

          {/* Acknowledgment */}
          <div className="mt-10 text-center">
            <p className="text-xs text-gray-600 leading-relaxed">
              By using WingmanFix, you acknowledge that you have read and understood this Privacy Policy and consent to the collection and use of your information as described herein.
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
